import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Button,
  ButtonGroup,
  Alert,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useSubscriptionDetails } from "../../hooks/useSubscriptionDetails";
import { useRemoveCustomPlan } from "../../hooks/useRemoveCustomPlan";
import CompanyNotes from "./CompanyNotes";
import InitialLoader from "components/InitilalLoader";
import moment from "moment";
import PaymentHistoryView from "../PaymentHistory/PaymentHistoryView";
import StatusHistoryView from "../PaymentHistory/StatusHistoryView";
import WebhookEventsView from "../PaymentHistory/WebhookEventsView";
import AccountHealthBanner from "./AccountHealthBanner";

const CompanySubscriptionDetails = ({
  companyId,
  onBack,
  onUpdatePrice,
  onCreateCustomPlan,
  onEditCustomPlan,
  onBlockCompany,
  onUpdatePaymentStatus,
}) => {
  const { data, isLoading, error } = useSubscriptionDetails(companyId);
  const { mutate: removeCustomPlan, isPending: isRemoving } =
    useRemoveCustomPlan();
  const [activeTab, setActiveTab] = useState("actions");

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "Paid":
        return "success";
      case "Unpaid":
        return "warning";
      case "Frozen1":
        return "danger";
      case "Frozen2":
        return "danger";
      case "Standard":
        return "info";
      case "Deactivated/Unsubscribed":
        return "dark";
      default:
        return "secondary";
    }
  };

  if (isLoading) {
    return <InitialLoader />;
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error loading subscription details: {error.message}
        <Button className="ms-2" variant="outline-secondary" onClick={onBack}>
          Back to List
        </Button>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert variant="info">
        No subscription details found.
        <Button className="ms-2" variant="outline-secondary" onClick={onBack}>
          Back to List
        </Button>
      </Alert>
    );
  }

  const {
    company,
    subscription,
    plan,
    stripeSubscription,
    lastLoginTime,
    hasSubscription,
  } = data;
  const hasCustomPlan = plan?.isCustomPlan || subscription?.customPlanEnabled;

  const handleRemoveCustomPlan = () => {
    if (
      window.confirm(
        "Are you sure you want to remove the custom plan? The company will revert to the master plan."
      )
    ) {
      removeCustomPlan(companyId, {
        onSuccess: () => {
          // Data will be refetched automatically
        },
      });
    }
  };

  return (
    <div>
      <div className="mb-3">
        <Button variant="outline-secondary" onClick={onBack}>
          ← Back to List
        </Button>
      </div>

      {hasSubscription !== false && (
        <AccountHealthBanner
          companyId={companyId}
          enabled={hasSubscription !== false}
        />
      )}

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>Company Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Company Name:</strong> {company?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {company?.email || "N/A"}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    <Badge bg={company?.isActive ? "success" : "danger"}>
                      {company?.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                  <p>
                    <strong>Phone 1:</strong>{" "}
                    {company?.phone1Code && company?.phone1
                      ? `${company.phone1Code} ${company.phone1}`
                      : "N/A"}
                  </p>
                  {company?.phone2 && (
                    <p>
                      <strong>Phone 2:</strong> {company?.phone2Code}{" "}
                      {company.phone2}
                    </p>
                  )}
                  {company?.fax && (
                    <p>
                      <strong>Fax:</strong> {company?.faxCode} {company.fax}
                    </p>
                  )}
                  {company?.website && (
                    <p>
                      <strong>Website:</strong>{" "}
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {company.website}
                      </a>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Company ID:</strong> {companyId}
                  </p>
                  <p>
                    <strong>Address:</strong> {company?.address1 || "N/A"}
                    {company?.address2 && `, ${company.address2}`}
                  </p>
                  <p>
                    <strong>City/State/Zip:</strong>{" "}
                    {[company?.city, company?.stateId, company?.zipCode]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </p>
                  {company?.county && (
                    <p>
                      <strong>County:</strong> {company.county}
                    </p>
                  )}
                  {company?.createdOn && (
                    <p>
                      <strong>Created On:</strong>{" "}
                      {moment(company.createdOn).format("MMM DD, YYYY")}
                    </p>
                  )}
                  {subscription?.blockedAt && (
                    <Alert variant="danger" className="mt-2">
                      <strong>Blocked:</strong>{" "}
                      {moment(subscription.blockedAt).format("MMM DD, YYYY")}
                      {subscription.blockedReason && (
                        <div>
                          <strong>Reason:</strong> {subscription.blockedReason}
                        </div>
                      )}
                    </Alert>
                  )}
                </Col>
              </Row>
              {!company?.billingAddressSameAsCompanyAddress &&
                company?.billingAddress1 && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <h6>Billing Address</h6>
                      <p>
                        {company.billingAddress1}
                        {company.billingAddress2 &&
                          `, ${company.billingAddress2}`}
                        <br />
                        {[
                          company.billingCity,
                          company.billingState,
                          company.billingZip,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </Col>
                  </Row>
                )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>Subscription Details</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Subscription ID:</strong>{" "}
                    {subscription?.subscriptionId || "N/A"}
                  </p>
                  <p>
                    <strong>Stripe Customer ID:</strong>{" "}
                    {subscription?.stripeCustomerId || "N/A"}
                  </p>
                  <p>
                    <strong>Activate:</strong>{" "}
                    <Badge bg={subscription?.activate ? "success" : "danger"}>
                      {subscription?.activate ? "Yes" : "No"}
                    </Badge>
                  </p>
                  <p>
                    <strong>Payment Status:</strong>{" "}
                    <Badge
                      bg={getStatusBadgeVariant(subscription?.paymentStatus)}
                    >
                      {subscription?.paymentStatus || "N/A"}
                    </Badge>
                  </p>
                </Col>
                <Col md={6}>
                  {stripeSubscription && (
                    <>
                      <p>
                        <strong>Stripe Status:</strong>{" "}
                        <Badge
                          bg={
                            stripeSubscription.status === "active"
                              ? "success"
                              : "warning"
                          }
                        >
                          {stripeSubscription.status}
                        </Badge>
                      </p>
                      <p>
                        <strong>Current Period:</strong>{" "}
                        {stripeSubscription.currentPeriodStart &&
                          moment(stripeSubscription.currentPeriodStart).format(
                            "MMM DD, YYYY"
                          )}{" "}
                        -{" "}
                        {stripeSubscription.currentPeriodEnd &&
                          moment(stripeSubscription.currentPeriodEnd).format(
                            "MMM DD, YYYY"
                          )}
                      </p>
                    </>
                  )}
                  {hasCustomPlan && (
                    <Alert variant="info" className="mt-2">
                      <strong>Custom Plan Active</strong>
                    </Alert>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {hasSubscription !== false && (
        <Card className="mb-3">
          <Card.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="actions" title="Actions">
                <ButtonGroup className="me-2 mb-2">
                  <Button
                    variant="primary"
                    onClick={() =>
                      onUpdatePrice(companyId, plan?.baseplanPrice, plan)
                    }
                  >
                    Update Price
                  </Button>
                  <Button
                    variant="info"
                    onClick={() =>
                      hasCustomPlan
                        ? onEditCustomPlan(companyId, plan)
                        : onCreateCustomPlan(companyId)
                    }
                  >
                    {hasCustomPlan ? "Edit Custom Plan" : "Create Custom Plan"}
                  </Button>
                  {hasCustomPlan && (
                    <Button
                      variant="warning"
                      onClick={handleRemoveCustomPlan}
                      disabled={isRemoving}
                    >
                      {isRemoving ? "Removing..." : "Remove Custom Plan"}
                    </Button>
                  )}
                </ButtonGroup>
                <ButtonGroup className="me-2 mb-2">
                  <Button
                    variant={subscription?.blockedAt ? "success" : "danger"}
                    onClick={() =>
                      onBlockCompany(companyId, !!subscription?.blockedAt)
                    }
                  >
                    {subscription?.blockedAt
                      ? "Unblock Company"
                      : "Block Company"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      onUpdatePaymentStatus(
                        companyId,
                        subscription?.paymentStatus
                      )
                    }
                  >
                    Update Payment Status
                  </Button>
                </ButtonGroup>
              </Tab>
              <Tab eventKey="payment" title="Payment History">
                {activeTab === "payment" && (
                  <PaymentHistoryView
                    companyId={companyId}
                    basePlan={plan}
                    isWaived={subscription?.paymentStatus === "Waived/Comp"}
                    enabled={hasSubscription !== false}
                  />
                )}
              </Tab>
              <Tab eventKey="statusHistory" title="Status History">
                {activeTab === "statusHistory" && (
                  <StatusHistoryView
                    companyId={companyId}
                    enabled={hasSubscription !== false}
                  />
                )}
              </Tab>
              <Tab eventKey="webhooks" title="Webhook Events">
                {activeTab === "webhooks" && (
                  <WebhookEventsView
                    companyId={companyId}
                    enabled={hasSubscription !== false}
                  />
                )}
              </Tab>
              <Tab eventKey="notes" title="Admin Notes">
                {activeTab === "notes" && (
                  <>
                    <Row>
                      <Col md={12}>
                        <CompanyNotes
                          companyId={companyId}
                          enabled={hasSubscription !== false}
                        />
                      </Col>
                    </Row>
                    <Row className="mt-3">
                      <Col md={12}>
                        <Card>
                          <Card.Header>
                            <h5>Activity</h5>
                          </Card.Header>
                          <Card.Body>
                            <p>
                              <strong>Last Login:</strong>{" "}
                              {lastLoginTime
                                ? moment(lastLoginTime).format(
                                    "MMM DD, YYYY h:mm A"
                                  )
                                : "Never"}
                            </p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </>
                )}
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CompanySubscriptionDetails;
